<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MasifiSettingsAPI from 'dashboard/api/masifiSettings';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';

// Sección unificada de settings propias de Masifi ("Masifi Inbox"). Cada knob del
// backend (Masifi::Settings::REGISTRY) se renderiza como una card. Hoy: aviso de duplicado.
const { t } = useI18n();

const isLoading = ref(true);
const duplicateOwnerAlert = ref(true);
const isolateLeads = ref(true);

const fetchSettings = async () => {
  isLoading.value = true;
  try {
    const { data } = await MasifiSettingsAPI.get();
    duplicateOwnerAlert.value = Boolean(
      data.settings?.duplicate_lead_owner_alert
    );
    isolateLeads.value = Boolean(data.settings?.isolate_leads_on_reassign);
  } catch (error) {
    useAlert(t('MASIFI_SETTINGS.API.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// Persiste un knob; revierte el toggle local si la API falla. Reusado por cada card.
const persist = async (key, value, model) => {
  try {
    await MasifiSettingsAPI.update({ [key]: value });
    useAlert(t('MASIFI_SETTINGS.API.SAVE_SUCCESS'));
  } catch (error) {
    model.value = !value; // revertir si falló
    useAlert(t('MASIFI_SETTINGS.API.SAVE_ERROR'));
  }
};

const updateDuplicateOwner = value =>
  persist('duplicate_lead_owner_alert', value, duplicateOwnerAlert);
const updateIsolateLeads = value =>
  persist('isolate_leads_on_reassign', value, isolateLeads);

onMounted(fetchSettings);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="t('MASIFI_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('MASIFI_SETTINGS.TITLE')"
        :description="t('MASIFI_SETTINGS.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-4 pb-8">
        <SettingsToggleSection
          v-model="duplicateOwnerAlert"
          :header="t('MASIFI_SETTINGS.DUPLICATE_OWNER.HEADER')"
          :description="t('MASIFI_SETTINGS.DUPLICATE_OWNER.DESCRIPTION')"
          @update:model-value="updateDuplicateOwner"
        />
        <SettingsToggleSection
          v-model="isolateLeads"
          :header="t('MASIFI_SETTINGS.ISOLATE_LEADS.HEADER')"
          :description="t('MASIFI_SETTINGS.ISOLATE_LEADS.DESCRIPTION')"
          @update:model-value="updateIsolateLeads"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
